/**
 * Custom Hooks for Error Handling
 * Provides easy-to-use hooks for handling errors in components
 * 
 * Features:
 * - useErrorHandler: Basic error handling hook
 * - useAsyncError: Hook for handling async operations with error handling
 * - useNetworkError: Hook specifically for network error handling
 * - useRetryableOperation: Hook for operations that can be retried
 */

import { useCallback, useState } from 'react';
import { useError, ERROR_TYPES } from '../components/shared/ErrorContext';

/**
 * Basic error handler hook
 * Usage: const handleError = useErrorHandler();
 */
export const useErrorHandler = () => {
  const { addError } = useError();

  return useCallback((error, type = ERROR_TYPES.UNKNOWN, options = {}) => {
    return addError(error, type, options);
  }, [addError]);
};

/**
 * Async error handler hook with loading state
 * Usage: const { execute, isLoading, error } = useAsyncError();
 */
export const useAsyncError = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const handleError = useErrorHandler();

  const execute = useCallback(async (asyncFunction, errorType = ERROR_TYPES.APPLICATION, errorOptions = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await asyncFunction();
      return result;
    } catch (err) {
      setError(err);
      handleError(err, errorType, errorOptions);
      throw err; // Re-throw for component-level handling if needed
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
  }, []);

  return {
    execute,
    isLoading,
    error,
    reset,
  };
};

/**
 * Network error handler hook with retry functionality
 * Usage: const { executeWithRetry, isLoading, retryCount } = useNetworkError();
 */
export const useNetworkError = (maxRetries = 3) => {
  const [isLoading, setIsLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const handleError = useErrorHandler();

  const executeWithRetry = useCallback(async (networkFunction, options = {}) => {
    const {
      showModal = false,
      showNotification = true,
      onRetryExhausted,
    } = options;

    setIsLoading(true);

    const attemptOperation = async (attempt = 0) => {
      try {
        setRetryCount(attempt);
        const result = await networkFunction();
        setRetryCount(0); // Reset on success
        return result;
      } catch (error) {
        if (attempt < maxRetries) {
          // Retry the operation
          const delay = Math.pow(2, attempt) * 1000; // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
          return attemptOperation(attempt + 1);
        } else {
          // Max retries reached
          setRetryCount(0);
          
          handleError(error, ERROR_TYPES.NETWORK, {
            showModal,
            showNotification,
            retryable: false,
            metadata: {
              maxRetriesReached: true,
              totalAttempts: attempt + 1,
            },
          });

          if (onRetryExhausted) {
            onRetryExhausted(error, attempt + 1);
          }

          throw error;
        }
      }
    };

    try {
      const result = await attemptOperation();
      return result;
    } finally {
      setIsLoading(false);
    }
  }, [maxRetries, handleError]);

  return {
    executeWithRetry,
    isLoading,
    retryCount,
    maxRetries,
  };
};

/**
 * Retryable operation hook
 * Usage: const { execute, retry, isLoading, canRetry } = useRetryableOperation();
 */
export const useRetryableOperation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastOperation, setLastOperation] = useState(null);
  const [lastError, setLastError] = useState(null);
  const handleError = useErrorHandler();

  const execute = useCallback(async (operation, errorType = ERROR_TYPES.APPLICATION, errorOptions = {}) => {
    setIsLoading(true);
    setLastOperation(() => operation);
    setLastError(null);

    try {
      const result = await operation();
      return result;
    } catch (error) {
      setLastError(error);
      
      const retryableOptions = {
        ...errorOptions,
        retryable: true,
        onRetry: () => retry(),
      };

      handleError(error, errorType, retryableOptions);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  const retry = useCallback(async () => {
    if (!lastOperation) {
      throw new Error('No operation to retry');
    }

    setIsLoading(true);
    setLastError(null);

    try {
      const result = await lastOperation();
      return result;
    } catch (error) {
      setLastError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [lastOperation]);

  const reset = useCallback(() => {
    setIsLoading(false);
    setLastOperation(null);
    setLastError(null);
  }, []);

  return {
    execute,
    retry,
    reset,
    isLoading,
    canRetry: !!lastOperation,
    lastError,
  };
};

/**
 * Form error handler hook with field-level error tracking
 * Usage: const { handleFieldError, clearFieldError, fieldErrors } = useFormErrorHandler();
 */
export const useFormErrorHandler = () => {
  const [fieldErrors, setFieldErrors] = useState({});
  const handleError = useErrorHandler();

  const handleFieldError = useCallback((fieldName, error, showGlobalError = false) => {
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error.message || error,
    }));

    if (showGlobalError) {
      handleError(error, ERROR_TYPES.VALIDATION, {
        showModal: false,
        showNotification: true,
        retryable: false,
        metadata: { fieldName },
      });
    }
  }, [handleError]);

  const clearFieldError = useCallback((fieldName) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  const clearAllFieldErrors = useCallback(() => {
    setFieldErrors({});
  }, []);

  const hasFieldError = useCallback((fieldName) => {
    return !!fieldErrors[fieldName];
  }, [fieldErrors]);

  const getFieldError = useCallback((fieldName) => {
    return fieldErrors[fieldName];
  }, [fieldErrors]);

  return {
    handleFieldError,
    clearFieldError,
    clearAllFieldErrors,
    hasFieldError,
    getFieldError,
    fieldErrors,
  };
};

export default {
  useErrorHandler,
  useAsyncError,
  useNetworkError,
  useRetryableOperation,
  useFormErrorHandler,
};